/* eslint-disable no-undef */
/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2023 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
/* eslint-disable no-underscore-dangle*/

// eslint-disable-next-line import/no-cycle
import { providers } from './Providers';
import { auth2 } from './Auth2API';
import { locale2 } from './Locale2API';
import { logging } from './LoggingAPI';

const logger = logging.getLogger('TargetAPI');

class TargetAPI {
  constructor(options) {
    this.alloyOn = window.adobe_dc_sdk?.launchOptions?.alloyOn;
    this._alloyInitialized = false;
    this.options = {};
    this._init = optns => {
      this.options = {
        mboxes: [],
        defaultParameters: {
          'data-language': locale2.getLocale(),
        },
        imsEnabled: true,
        ...optns,
      };
      this._offers = { execute: { mboxes: [] } };
    };
    this._initAlloy = optns => {
      this._alloyInitialized = true;
      const optionsCopy = optns;
      if (optionsCopy?.mboxes && !optionsCopy?.decisionScopes) {
        Object.defineProperty(
          optionsCopy, 'decisionScopes',
          Object.getOwnPropertyDescriptor(optionsCopy, 'mboxes'),
        );
        delete optionsCopy.mboxes;
      }
      this.options = {
        decisionScopes: [],
        defaultParameters: {
          language: locale2.getLocale(),
        },
        imsEnabled: true,
        ...optionsCopy,
      };
      this._offers = { propositions: [] };
    };
    this._init = this._init.bind(this);
    this._initAlloy = this._initAlloy.bind(this);
    this._targetPromise = window.__targetLoadedPromise || new window.adobe_dc_sdk.util.Deferred();
    const replaceGetOffer = () => {
      // Replace getOffer method with custom one to avoid exception from error callback
      const g = window.adobe.target.getOffer;
      window.adobe.target.getOffer = params => {
        const p = params;
        p.error = () => {};
        return g(p);
      };
    };
    const targetPromiseHandler = (privileged, targetTimer) => {
      if (privileged) {
        this._targetPromise.reject(new Error('Target disabled for PAC user'));
        clearInterval(targetTimer);
        return;
      }
      if (this._alloyInitialized) {
        clearInterval(targetTimer);
        this._targetPromise.resolve(window.alloy);
      } else if (window.adobe.target) {
        replaceGetOffer();
        clearInterval(targetTimer);
        this._targetPromise.resolve(window.adobe.target);
      }
    };
    const handlePrivilege = targetTimer => {
      providers.limits2().then(limits2 => {
        targetPromiseHandler(limits2.isPrivilegedAction('marketing-integration'), targetTimer);
      });
    };

    const initTarget = (optns, targetTimer) => {
      this._init(optns);
      // If target is not enabled, reject promise instead of waiting
      if (window.adobe.optIn?.isApproved('target') === false) {
        this._targetPromise.reject(new Error('Target opted out by user'));
        clearInterval(targetTimer);
        return;
      }
      if (!auth2.isSignedIn && window.adobe.target) {
        replaceGetOffer();
        this._targetPromise.resolve(window.adobe.target);
        clearInterval(targetTimer);
        return;
      }
      handlePrivilege(targetTimer);
    };

    const initAlloyTarget = (optns, targetTimer) => {
      this._initAlloy(optns);
      this.isUserConsentForTarget().then(userConsent => {
        if (!userConsent) {
          this._targetPromise.reject(new Error('Target opted out by user'));
          clearInterval(targetTimer);
          return;
        }
        if (!auth2.isSignedIn) {
          this._targetPromise.resolve(window.alloy);
          clearInterval(targetTimer);
          return;
        }
        handlePrivilege(targetTimer);
      }).catch(e => {
        logger.error(e.message);
        this._targetPromise.reject(new Error(`alloy initialization error: ${e.message}`));
        clearInterval(targetTimer);
      });
    };

    if (window.adobe?.target) {
      initTarget(options);
    } else if (this.alloyOn && window.alloy) {
      initAlloyTarget(options);
    } else {
      const targetTimer = setInterval(() => {
        if (this.alloyOn && window.alloy) {
          initAlloyTarget(options, targetTimer);
        } else if (window.adobe) {
          initTarget(options, targetTimer);
        }
      }, 1000);
    }
  }

  /* for alloy, we are using adobe privacy
   * performance cookie (COO2) for user target consent
  */
  isUserConsentForTarget = async () => {
    try {
      await window.adobe_dc_sdk.loaded.adobePrivacyReady.promise;
      const activeGroups = window.adobePrivacy?.activeCookieGroups();
      if (activeGroups?.indexOf('C0002') !== -1) {
        return true;
      }
      return false;
    } catch (error) {
      throw new Error(`isUserConsentForTarget error: ${error}`);
    }
  };

  fetchProposition = (decisionScopes, decisionScopeParams) => window.alloy('sendEvent', {
    decisionScopes,
    type: 'decisioning.propositionFetch', // This is used to identify alloy event is a target fetch call
    renderDecisions: false,
    personalization: {
      sendDisplayEvent: false,
    },
    data: {
      __adobe: {
        target: decisionScopeParams,
      },
    },
  });

  /* when using the alloy web SDK we need to
  send target payload back to analytics for
  reporting correct impressions and conversions */
  sendDisplayEvent = displayPropositions => window.alloy('sendEvent', {
    xdm: {
      eventType: 'decisioning.propositionDisplay',
      _experience: {
        decisioning: {
          propositions: displayPropositions, // This is used to identify alloy event is a target display` call
        },
      },
    },
  });

  /**
     * @returns promise that resolves when target provider has been instantiated
     */
  ready = async () => {
    const self = this;
    const imsPromise = this.options.imsEnabled ? window.adobe_dc_sdk.loaded.imsReady : Promise.resolve();
    await imsPromise;
    let subscriptionsPromise = Promise.resolve({
      identity: undefined,
      subscriptions: {
        subscriptions: [],
      },
    });

    if (auth2.isSignedIn) {
      subscriptionsPromise = providers.user().then(u => u.getUser());
    }

    try {
      const [t, { identity, subscriptions }] = await Promise.all([this._targetPromise.promise, subscriptionsPromise]);
      const s = subscriptions.subscriptions;

      const countryCode = identity ? identity.country_code : '';
      const language = identity ? identity.language : locale2.getLocale();
      const firstTimeUser = identity && identity.first_time && identity.first_time.dex_web_app;
      const subscriptionLevel = s.length > 0 ? s[0].level : '';
      const subscriptionName = s.length > 0 ? s[0].name : '';
      const userRole = s.length > 0 && s[0].level !== 'Free' ? s[0].name : '';

      if (self._alloyInitialized) {
        self._alloy = t;
        self.options.defaultParameters = {
          countryCode,
          language,
          firstTimeUser,
          subscriptionLevel,
          subscriptionName,
          userRole,
        };
        const decisionScopes = self.options.decisionScopes.map(m => m.name);
        const decisionScopeParams = Object.assign(
          self.options.defaultParameters,
          self.options.decisionScopes.map(m => m.parameters) || {},
        );
        const response = await this.fetchProposition(decisionScopes, decisionScopeParams);
        self._offers = response;
        const { propositions } = response;
        if (propositions) {
          /* we need to make sure to use page view sent promise,
          so that it won't impact any Analytics data. */
          if (window.alloy_pageView?.sent) {
            window.alloy_pageView.sent
              .then(() => this.sendDisplayEvent(propositions));
          } else {
            this.sendDisplayEvent(propositions);
          }
        }
        return self;
      }
      self._target = t;
      self.options.defaultParameters = {
        'data-countryCode': countryCode,
        'data-language': language,
        'data-firstTimeUser': firstTimeUser,
        'data-subscriptionlevel': subscriptionLevel,
        'data-subscriptionName': subscriptionName,
        'data-userRole': userRole,
      };

      const mboxes = self.options.mboxes;
      mboxes.map((m, i) => {
        const mbox = m;
        mbox.parameters = Object.assign(self.options.defaultParameters, m.parameters || {});
        mbox.index = i;
        return mbox;
      });
      const consumerId = self.options.mboxes.map(m => m.name).join(',');
      const response = await self._target.getOffers({
        consumerId,
        request: {
          execute: {
            mboxes,
          },
        },
      });
      self._offers = response;
      return self;
    } catch (error) {
      logger.warn('error initializing target');
      return self;
    }
  }

    /**
     * Check if a given offer is enabled
     * @param {string} mboxId - The mboxid to check
     * @param {object} options
     *        options.strictCheck {boolean} - Use strict checking
     * @returns {true|false|undefined} -
     *  When options.strictCheck is true:
     *    - true when the user is in test challenger group.
     *    - false when the user in test control group.
     *    - undefined when the user is not considered for test.
     *  When options.strictCheck is false:
     *    - true when the user is in test challenger group.
     *    - false otherwise
     */
    isOfferEnabled = (mboxId, { strictCheck = false } = {}) => {
      if (!mboxId) {
        if (strictCheck) {
          return undefined;
        }
        return false;
      }
      if (this._alloyInitialized) {
        const propositions = this._offers.propositions.filter(p => p.scope === mboxId);
        if (propositions?.length) {
          const { items = [] } = propositions[0];
          if (items.length === 0 && strictCheck) {
            return undefined;
          }
          return items.some(i => i.data?.content?.enabled);
        }
        if (strictCheck) {
          return undefined;
        }
        return false;
      }
      const mboxes = this._offers.execute.mboxes.filter(m => m.name === mboxId);
      if (mboxes?.length) {
        const { options = [] } = mboxes[0];
        if (options.length === 0 && strictCheck) {
          return undefined;
        }
        return options.some(o => o.content?.enabled);
      }
      if (strictCheck) {
        return undefined;
      }
      return false;
    };

    getOffer = async ({ mboxId, params = {}, getPageLoadOffers = false }) => {
      if (!this?._target && !this?._alloy) {
        logger.info('Target not loaded');
        return [{}];
      }

      if (this?._alloy) {
        const decisionScopeParams = Object.assign(this.options.defaultParameters, params);
        let response = [{}];
        if (getPageLoadOffers) {
          try {
            const { propositions = [] } = await this.fetchProposition(['__view__'], decisionScopeParams);
            response = propositions;
          } catch (e) {
            logger.error(`Error: TargetAPI alloy sendEvent getPageLoadOffers: ${e}`);
          }
          if (response?.length) {
            this.sendDisplayEvent(response);
          }
          return response;
        }
        try {
          const { propositions = [] } = await this.fetchProposition([mboxId], decisionScopeParams);
          response = propositions;
        } catch (e) {
          logger.error(`Error: TargetAPI alloy sendEvent: ${e}`);
        }
        const resultProposition = response.filter(p => p.scope === mboxId);
        const { content } = resultProposition?.[0]?.items?.[0]?.data || {};
        if (resultProposition?.[0]) {
          this.sendDisplayEvent(resultProposition);
        }
        return [{
          content: content && [content],
        }];
      }

      const getOffersParams = {
        request: {
          execute: {
          },
        },
      };

      if (getPageLoadOffers) {
        getOffersParams.request.execute.pageLoad = {};
      } else {
        const offerParams = Object.assign(this.options.defaultParameters, params);
        const mboxes = [{
          name: mboxId,
          parameters: offerParams,
          index: 0,
        }];
        getOffersParams.consumerId = mboxId;
        getOffersParams.request.execute.mboxes = mboxes;
      }
      let response = [{}];
      try {
        const { execute = {} } = await window.adobe.target.getOffers(getOffersParams);
        response = execute;
      } catch (e) {
        logger.error(`Error: TargetAPI.getOffers: ${e}`);
      }

      if (getPageLoadOffers) return response;

      const { content } = response.mboxes?.[0]?.options?.[0] || {};
      return [{
        content: content && [content],
      }];
    };
}

export default TargetAPI;
